package com.example.mystybag.login.logic

import com.example.mystybag.login.logic.NavigatorMerchantRegistrationUnderReview
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.Response
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.expected.byteArrayFromFile
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.expected.VibratorHelper
import com.example.mystybag.core.helpers.GlobalCommonData.setReferralCodeValue
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.security.Security
import com.example.mystybag.login.model.KeyButtonType
import com.example.mystybag.login.model.MerchantRegistrationCheckInboxUIState
import com.example.mystybag.sharedmodels.base.BaseResponseModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.CreateMerchantRequestModel
import kotlinx.coroutines.launch

class MerchantRegistrationCheckInboxViewModel(
    context: AppContext,
    private val config: NavigatorMerchantRegistrationStep5
) : ChildViewModel<MerchantRegistrationCheckInboxUIState>(context, MerchantRegistrationCheckInboxUIState()) {

    // Note: merchantId will be 0 initially, will be set after verification

    init {
        updateState {
            it.copy(email = config.email)
        }
    }

    override fun onCreate() {
        navControls.backButtonVisible = false
    }

    fun updateVerificationCode(code: String) {
        val numericCode = code.filter { it.isDigit() }.take(6)
        updateState { it.copy(verificationCode = numericCode) }
        if (numericCode.length == 6) {
            verifyCode(numericCode)
        }
    }

    fun onKeyboardClick(key: KeyButtonType) {
        if (uiState.value.verificationCode.length == 6 && key != KeyButtonType.TYPE_BACK)
            return
        if (key == KeyButtonType.TYPE_FINGERPRINT || key == KeyButtonType.TYPE_FACE)
            return
        if (key == KeyButtonType.TYPE_BACK) {
            if (uiState.value.verificationCode.isNotEmpty())
                VibratorHelper.vibrate()
            updateState { it.copy(verificationCode = uiState.value.verificationCode.dropLast(1)) }
        } else if (uiState.value.verificationCode.length < 6) {
            VibratorHelper.vibrate()
            val newCode = uiState.value.verificationCode.plus(key.value)
            updateState { it.copy(verificationCode = newCode) }
            if (newCode.length == 6) {
                verifyCode(newCode)
            }
        }
    }

    private fun verifyCode(code: String) {
        if (code.length != 6 || uiState.value.isLoading) {
            return
        }

        scope.launch {
            updateState { it.copy(isLoading = true) }
            
            // Load company image if available
            val companyImageBytes = config.companyImageFileName?.let { fileName ->
                byteArrayFromFile(fileName)
            }
            
            commandBlock(showProgress = true) {
                CommonCommands.createMerchant(
                    CreateMerchantRequestModel(
                        // Company details (for merchants table)
                        companyName = config.legalEntityName, // Will create translation key on server
                        email = config.email,
                        password = Security.hashPasswordForRequest(config.password),
                        dialCode = config.companyDialCode, // Using company's dial code for merchant
                        phoneNumber = config.companyPhoneNumber, // Using company's phone for merchant
                        bulstat = config.bulstat,
                        iban = config.iban,
                        countryId = config.registeredCompanyCountryId!!, // Required
                        cityId = config.registeredCompanyCityId!!, // Required
                        privacyPolicyAccepted = config.privacyPolicyAccepted,
                        companyStreet = config.registeredCompanyStreet, // maps to merchants.company_street (nullable)
                        companyImage = companyImageBytes, // Company logo/image
                        
                        // Company signatory details (for company_responsible_persons table) - convert to API format
                        signatories = config.signatories.map { signatory ->
                            com.example.mystybag.sharedmodels.project.requestModels.merchant.SignatoryItem(
                                firstName = signatory.firstName,
                                lastName = signatory.lastName,
                                phoneNumber = signatory.phoneNumber,
                                dialCode = signatory.dialCode
                            )
                        },
                        
                        // Verification code (required when creating merchant after email verification)
                        verificationCode = code,
                        phoneVerificationCode = config.phoneVerificationCode,
                        referralCode = config.referralCode,
                        paymentProvider = config.paymentProvider
                    )
                )
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                setReferralCodeValue("")
                                // Registration is pending review by a staff member -
                                // show the under-review screen instead of the success screen.
                                navigate(NavigatorMerchantRegistrationUnderReview)
                            }
                        },
                        baseError = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false, verificationCode = "") }
                                showSnackbar(Response.getErrorMessage(response.status, response.apiEndPoint, response.statusMessage))
                            }
                        }
                    )
            }
        }
    }
}
